import React, { useState, useEffect, useRef } from 'react';

export default function AiDesignerChatPanel({
  mode = 'card', // 'card' | 'face'
  currentState = {},
  availableAssets = [],
  onApplyMutations,
  onClose,
  tenantConfig = null,
  user = null,
  token = null
}) {
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const activeToken = token || (typeof window !== 'undefined' ? (sessionStorage.getItem('token') || localStorage.getItem('token')) : '');

  const storageKey = `trophycoin_ai_chat_history_${mode}`;
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load saved chat history:', e);
    }
    return [
      {
        id: 'welcome',
        role: 'assistant',
        content: mode === 'card'
          ? "Hello! I'm your **Trophy Card Design Copilot**. I can craft luxury backgrounds, align typography, add certification QR badges, create data tables, and apply tournament color palettes. What would you like to design today?"
          : "Hello! I'm your **3D Coin Face Design Copilot**. I can help you engrave classic numismatic coins, configure reeded and beaded concentric rings, curve inscriptions, and style precious metal finishes. How can I help with your coin face?"
      }
    ];
  });

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [appliedProposalIds, setAppliedProposalIds] = useState(new Set());
  const [previousStates, setPreviousStates] = useState({});

  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch (e) {
      console.warn('Failed to persist chat messages to localStorage', e);
    }
  }, [messages, storageKey]);

  // Scroll to bottom within panel when messages update (isolated to panel container, never scrolling the window)
  useEffect(() => {
    if (messagesContainerRef.current) {
      if (typeof messagesContainerRef.current.scrollTo === 'function') {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }
  }, [messages, isLoading]);

  const quickPrompts = mode === 'card' ? [
    { label: '✨ Luxury Gold Foil', prompt: 'Style this card with a luxury black and gold foil theme, elegant serif fonts, and gold accents.' },
    { label: '⚓ Vintage Nautical', prompt: 'Create an authentic vintage nautical tournament card with deep navy background, weathered border, and maritime typography.' },
    { label: '📱 Add QR & Verify', prompt: 'Add an official verification QR code block and certification stamp to the back side of the card.' },
    { label: '📊 Clean Stat Grid', prompt: 'Add a clean, structured angler catch statistics grid with labels for Species, Weight, Length, and Date.' },
    { label: '🎨 Modern Minimalist', prompt: 'Clean up the layout with a modern minimalist aesthetic, ample breathing room, and crisp contrast.' }
  ] : [
    { label: '🪙 Antique Gold Mint', prompt: 'Make this an antique gold commemorative coin with double outer reeded rims and deep relief.' },
    { label: '⚓ Maritime Reeded', prompt: 'Design a nautical coin face with rope edge, curved upper text "WORLD ANGLER CHAMPIONSHIP", and clean bottom date.' },
    { label: '🏆 Championship Medal', prompt: 'Create a prestigious championship medal in bright silver with bold serif curved lettering and double concentric rings.' },
    { label: '🔤 Optimize Typography', prompt: 'Balance the curved typography with optimal radius, uppercase lettering, and clean letter-spacing.' }
  ];

  const handleClearHistory = () => {
    const welcome = [
      {
        id: 'welcome',
        role: 'assistant',
        content: mode === 'card'
          ? "Chat reset. How would you like to style or modify your Trophy Card?"
          : "Chat reset. How would you like to configure your 3D Coin Face?"
      }
    ];
    setMessages(welcome);
    setAppliedProposalIds(new Set());
    setPreviousStates({});
    try {
      localStorage.setItem(storageKey, JSON.stringify(welcome));
    } catch (e) {}
  };

  const handleSendMessage = async (textToSend) => {
    const promptText = (textToSend || inputValue).trim();
    if (!promptText || isLoading) return;

    setErrorMessage(null);
    const userMsgId = `user_${Date.now()}`;
    const userMsg = { id: userMsgId, role: 'user', content: promptText };

    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputValue('');
    setIsLoading(true);

    if (!activeToken) {
      setErrorMessage("Authentication token not found. Please log in or refresh your session.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/ai-designer/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${activeToken}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          mode,
          prompt: promptText,
          current_state: currentState,
          history: newMessages.slice(-8).map(m => ({ role: m.role, content: m.content })),
          available_assets: (availableAssets || []).slice(0, 25).map(a => ({
            id: a.id,
            filename: a.filename || a.name,
            asset_type: a.asset_type || a.type,
            file_url: a.file_url || a.url
          })),
          tenant_id: tenantConfig?.id || user?.tenant_id || null
        })
      });

      if (!response.ok) {
        let errData;
        try {
          errData = await response.json();
        } catch (_) {
          errData = { detail: `Server error (${response.status})` };
        }
        throw new Error(errData.detail || 'Failed to generate design proposal');
      }

      const data = await response.json();
      const assistantMsgId = `assistant_${Date.now()}`;
      const assistantMsg = {
        id: assistantMsgId,
        role: 'assistant',
        content: data.message,
        suggested_actions: data.suggested_actions || [],
        canvas_mutations: data.canvas_mutations || null
      };

      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      console.error('AI Designer error:', err);
      setErrorMessage(err.message || 'Could not communicate with AI Copilot. Please check your connection or Gemini API key.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleApply = (msgId, mutations) => {
    if (!mutations || !onApplyMutations) return;
    
    // Store snapshot of currentState for reverting
    setPreviousStates(prev => ({
      ...prev,
      [msgId]: JSON.parse(JSON.stringify(currentState))
    }));

    onApplyMutations(mutations);
    setAppliedProposalIds(prev => new Set([...prev, msgId]));
  };

  const handleRevert = (msgId) => {
    const prevState = previousStates[msgId];
    if (prevState && onApplyMutations) {
      onApplyMutations({ __revert_state: prevState });
      setAppliedProposalIds(prev => {
        const next = new Set(prev);
        next.delete(msgId);
        return next;
      });
    }
  };

  // Helper to format changes summary
  const renderMutationsSummary = (mutations) => {
    if (!mutations) return null;
    const items = [];

    if (mode === 'card') {
      if (mutations.globalStyles) {
        const gs = mutations.globalStyles;
        const parts = [];
        if (gs.backgroundColor) parts.push(`Background: ${gs.backgroundColor}`);
        if (gs.borderColor) parts.push(`Border: ${gs.borderColor}`);
        if (gs.fontFamily) parts.push(`Font: ${gs.fontFamily}`);
        items.push(`🎨 Styles (${parts.join(', ')})`);
      }
      if (mutations.added_elements && mutations.added_elements.length > 0) {
        const types = mutations.added_elements.map(e => e.type || 'element').join(', ');
        items.push(`➕ Added ${mutations.added_elements.length} element(s) [${types}]`);
      }
      if (mutations.updated_elements && mutations.updated_elements.length > 0) {
        items.push(`✏️ Updated ${mutations.updated_elements.length} element(s)`);
      }
      if (mutations.replace_elements) {
        items.push(`🔄 Replaces layout with new composition`);
      }
    } else {
      if (mutations.metal) items.push(`🪙 Metal: ${mutations.metal.replace('_', ' ').toUpperCase()}`);
      if (mutations.upperText !== undefined) items.push(`🔤 Upper Text: "${mutations.upperText}"`);
      if (mutations.lowerText !== undefined) items.push(`🔤 Lower Text: "${mutations.lowerText}"`);
      if (mutations.rings && mutations.rings.length > 0) items.push(`⭕ ${mutations.rings.length} Concentric Rings`);
      if (mutations.edgeStyle) items.push(`⚙️ Edge: ${mutations.edgeStyle}`);
      if (mutations.suggested_symbol_search) items.push(`🔍 Suggested Emblem: "${mutations.suggested_symbol_search}"`);
    }

    if (items.length === 0) return null;

    return (
      <div style={{
        marginTop: '8px',
        padding: '8px 10px',
        backgroundColor: '#161619',
        borderRadius: '6px',
        border: '1px solid #32323a',
        fontSize: '11px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        color: '#c4c4cc'
      }}>
        <div style={{ fontWeight: '600', color: '#bdb2ff', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span>✨ Design Proposal</span>
        </div>
        {items.map((it, idx) => (
          <div key={idx} style={{ lineHeight: '1.3' }}>• {it}</div>
        ))}
      </div>
    );
  };

  return (
    <div style={{
      width: '350px',
      height: '100%',
      maxHeight: '100%',
      minHeight: 0,
      flexShrink: 0,
      backgroundColor: '#1e1e22',
      borderRight: '1px solid #3c3c42',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      boxSizing: 'border-box',
      userSelect: 'none',
      zIndex: 20,
      boxShadow: '4px 0 16px rgba(0, 0, 0, 0.25)',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* HEADER */}
      <div style={{
        padding: '12px 14px',
        backgroundColor: '#232327',
        borderBottom: '1px solid #323238',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '26px',
            height: '26px',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #bdb2ff 0%, #a0c4ff 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#121214',
            boxShadow: '0 2px 8px rgba(189, 178, 255, 0.35)'
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#f3f3f5', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>AI Copilot</span>
              <span style={{
                fontSize: '9px',
                padding: '1px 5px',
                borderRadius: '4px',
                backgroundColor: 'rgba(189, 178, 255, 0.15)',
                color: '#bdb2ff',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                fontWeight: '600'
              }}>
                {mode === 'card' ? 'Trophy Card' : 'Coin Face'}
              </span>
            </div>
            <div style={{ fontSize: '10px', color: '#8e8e93' }}>Gemini 2.5 Flash Designer</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            type="button"
            onClick={handleClearHistory}
            title="Reset Chat History"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#8e8e93',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#dfdfe3'}
            onMouseLeave={e => e.currentTarget.style.color = '#8e8e93'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </button>

          <button
            type="button"
            onClick={onClose}
            title="Close AI Panel"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#8e8e93',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
            onMouseLeave={e => e.currentTarget.style.color = '#8e8e93'}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* QUICK SUGGESTIONS CAROUSEL / CHIPS */}
      <div style={{
        padding: '8px 12px',
        backgroundColor: '#1b1b1e',
        borderBottom: '1px solid #2a2a30',
        display: 'flex',
        gap: '6px',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        scrollbarWidth: 'none',
        flexShrink: 0
      }}>
        {quickPrompts.map((qp, idx) => (
          <button
            type="button"
            key={idx}
            disabled={isLoading}
            onClick={() => handleSendMessage(qp.prompt)}
            style={{
              padding: '4px 9px',
              fontSize: '11px',
              borderRadius: '12px',
              backgroundColor: '#27272c',
              border: '1px solid #383842',
              color: '#c7c7cf',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              transition: 'all 0.15s',
              opacity: isLoading ? 0.6 : 1
            }}
            onMouseEnter={e => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = '#33333b';
                e.currentTarget.style.borderColor = '#bdb2ff';
                e.currentTarget.style.color = '#ffffff';
              }
            }}
            onMouseLeave={e => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = '#27272c';
                e.currentTarget.style.borderColor = '#383842';
                e.currentTarget.style.color = '#c7c7cf';
              }
            }}
          >
            {qp.label}
          </button>
        ))}
      </div>

      {/* ERROR BANNER */}
      {errorMessage && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#fca5a5',
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          lineHeight: '1.4',
          flexShrink: 0
        }}>
          <span>{errorMessage}</span>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            style={{ background: 'transparent', border: 'none', color: '#fca5a5', cursor: 'pointer', padding: '0 4px' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* MESSAGES LIST */}
      <div 
        ref={messagesContainerRef}
        data-testid="ai-designer-messages-container"
        className="ai-designer-messages-container"
        style={{
          flex: 1,
          minHeight: 0,
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '14px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          userSelect: 'text',
          boxSizing: 'border-box',
          scrollbarWidth: 'thin',
          scrollbarColor: '#4e4e58 #1e1e22'
        }}
      >
        <style>{`
          .ai-designer-messages-container::-webkit-scrollbar {
            width: 6px;
          }
          .ai-designer-messages-container::-webkit-scrollbar-track {
            background: #18181b;
          }
          .ai-designer-messages-container::-webkit-scrollbar-thumb {
            background: #3e3e46;
            border-radius: 4px;
          }
          .ai-designer-messages-container::-webkit-scrollbar-thumb:hover {
            background: #5c5c68;
          }
        `}</style>
        {messages.map((m) => {
          const isUser = m.role === 'user';
          const hasMutations = Boolean(m.canvas_mutations);
          const isApplied = appliedProposalIds.has(m.id);

          return (
            <div
              key={m.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isUser ? 'flex-end' : 'flex-start',
                maxWidth: '100%'
              }}
            >
              <div style={{
                maxWidth: '90%',
                padding: '9px 12px',
                borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                backgroundColor: isUser ? '#3b3363' : '#27272c',
                color: isUser ? '#ffffff' : '#dfdfe3',
                fontSize: '12px',
                lineHeight: '1.45',
                border: isUser ? '1px solid #4f46e5' : '1px solid #36363f',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
              }}>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {m.content}
                </div>

                {/* Structured proposal summary and apply button */}
                {hasMutations && (
                  <div>
                    {renderMutationsSummary(m.canvas_mutations)}
                    <div style={{ marginTop: '8px', display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => handleApply(m.id, m.canvas_mutations)}
                        disabled={isApplied}
                        style={{
                          flex: 1,
                          padding: '6px 10px',
                          borderRadius: '5px',
                          border: 'none',
                          backgroundColor: isApplied ? '#10b981' : '#bdb2ff',
                          color: isApplied ? '#ffffff' : '#141416',
                          fontWeight: '600',
                          fontSize: '11px',
                          cursor: isApplied ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '5px',
                          transition: 'all 0.15s'
                        }}
                      >
                        {isApplied ? '✓ Applied to Canvas' : `✨ Apply to ${mode === 'card' ? 'Card' : 'Face'}`}
                      </button>

                      {isApplied && (
                        <button
                          type="button"
                          onClick={() => handleRevert(m.id)}
                          title="Revert these changes"
                          style={{
                            padding: '6px 8px',
                            borderRadius: '5px',
                            border: '1px solid #4a4a55',
                            backgroundColor: '#202024',
                            color: '#e4e4e7',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px'
                          }}
                        >
                          ↩ Revert
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Suggested follow-up prompt chips */}
              {m.suggested_actions && m.suggested_actions.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  marginTop: '6px',
                  maxWidth: '92%'
                }}>
                  {m.suggested_actions.map((act, i) => (
                    <button
                      type="button"
                      key={i}
                      disabled={isLoading}
                      onClick={() => handleSendMessage(act)}
                      style={{
                        padding: '3px 8px',
                        fontSize: '10px',
                        borderRadius: '10px',
                        backgroundColor: 'rgba(189, 178, 255, 0.08)',
                        border: '1px solid rgba(189, 178, 255, 0.25)',
                        color: '#bdb2ff',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(189, 178, 255, 0.2)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(189, 178, 255, 0.08)'}
                    >
                      {act}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#bdb2ff', fontSize: '12px', padding: '6px 10px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid rgba(189, 178, 255, 0.2)',
              borderTopColor: '#bdb2ff',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <span>Copilot is designing...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* INPUT FOOTER */}
      <div style={{
        padding: '10px 12px',
        backgroundColor: '#232327',
        borderTop: '1px solid #323238',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end',
        flexShrink: 0
      }}>
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={mode === 'card' ? "Describe card layout, styles, or elements..." : "Describe coin face, rings, metals, or text..."}
          rows={2}
          style={{
            flex: 1,
            backgroundColor: '#18181b',
            border: '1px solid #3c3c44',
            borderRadius: '6px',
            color: '#f4f4f5',
            padding: '8px 10px',
            fontSize: '12px',
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
            lineHeight: '1.4'
          }}
          onFocus={e => e.currentTarget.style.borderColor = '#bdb2ff'}
          onBlur={e => e.currentTarget.style.borderColor = '#3c3c44'}
        />

        <button
          type="button"
          onClick={() => handleSendMessage()}
          disabled={isLoading || !inputValue.trim()}
          title="Send to AI Copilot"
          style={{
            height: '36px',
            width: '36px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: inputValue.trim() && !isLoading ? '#bdb2ff' : '#2e2e34',
            color: inputValue.trim() && !isLoading ? '#141416' : '#71717a',
            cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s',
            flexShrink: 0
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
}
